import {
  DEFAULT_DATE_FORMAT_FNS,
  EvaluationType,
  FilterKeys,
  GetReportsQueryFilters,
  ReportStatuses,
} from '@schooly/api';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export type UseReportsFiltersProps = {
  filters: GetReportsQueryFilters;
  onSetFilters: (filters: GetReportsQueryFilters) => void;
};

export const useReportsFilters = ({ filters, onSetFilters }: UseReportsFiltersProps) => {
  const { formatMessage } = useIntl();

  const actions = useMemo(() => {
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetReportStatus = (v: ReportStatuses[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.ReportStatus]: v,
      });
    };

    const onSetFiltersForKey = (key: keyof typeof filters) => (v: string[]) => {
      onSetFilters({
        ...filters,
        [key]: v,
      });
    };

    const onSetEvaluationType = (v: EvaluationType[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.EvaluationType]: v,
      });
    };

    return {
      onSetDate: (v: [Date, Date]) =>
        onSetFilters({
          ...filters,
          [FilterKeys.Date]: [
            format(v[0], DEFAULT_DATE_FORMAT_FNS),
            format(v[1], DEFAULT_DATE_FORMAT_FNS),
          ],
        }),
      onSetReportStatus,
      onClearReportStatus: clearFilter(FilterKeys.ReportStatus),
      onSetStaff: onSetFiltersForKey(FilterKeys.Staff),
      onClearStaff: clearFilter(FilterKeys.Staff),
      onSetEvaluationType,
      onClearEvaluationType: () => {
        onSetFilters({
          ...filters,
          [FilterKeys.EvaluationType]: [],
          [FilterKeys.AreaOfLearning]: [],
        });
      },
      onSetAreaOfLearning: onSetFiltersForKey(FilterKeys.AreaOfLearning),
      onClearAreaOfLearning: clearFilter(FilterKeys.AreaOfLearning),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date;
  const filtersReportStatus = filters.report_status;
  const filtersStaff = filters.staff;
  const filtersEvaluationType = filters.evaluation_type;
  const filtersAreaOfLearning = filters.area_of_learning;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-Period' }),
    [FilterKeys.ReportStatus]: formatMessage({ id: 'assessments-Status' }),
    [FilterKeys.Staff]: formatMessage({ id: 'schoolProperty-Staff' }),
    [FilterKeys.EvaluationType]: formatMessage({ id: 'assessments-AoLTutorsFeedback' }),
    [FilterKeys.AreaOfLearning]: formatMessage({ id: 'assessments-AoLTutorsFeedback' }),
  };

  return {
    ...actions,
    labels,
    filtersDate,
    filtersReportStatus,
    filtersStaff,
    filtersEvaluationType,
    filtersAreaOfLearning,
  };
};
